import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import styled from '@emotion/styled';

import { useQuery } from '@apollo/client/react';

import {
  DoorToDoor__BundleEnum,
  GeocodeDocument,
  Lead__ServiceNeeds,
} from '@graphql/platform';

import { AbandonmentModal } from '@root/components/checkout/abandonment_modal';
import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { useThirdPartyEventParams } from '@root/components/checkout/hooks/use_third_party_event_params';
import { MovingRedirectDialog } from '@root/components/checkout/product_pages/moving_redirect_dialog';
import { Modal as CouponModal } from '@root/components/checkout/product_pages/subcomponents/coupon/modal';
import {
  CheckoutVersion,
  StorageCheckoutStep,
} from '@root/components/checkout/types';
import { WTProvider, useTrack } from '@root/initializers/wt';
import { Commitment as CommitmentType } from '@root/resources/types/commitment';
import { PlanName } from '@root/resources/types/plan_name';
import { ServiceEnum } from '@root/resources/types/service';
import { EventSchema } from '@root/resources/wt/types';
import { WWW_ROUTES } from '@root/root/routes';

import { useClientDataContext } from '@shared/client_data_context';
import { useCreateLead } from '@shared/lead_form';
import { ServiceSelection } from '@shared/service_selector/constants';

import { useFlowInitiatedFunnelEvents } from '@utils/hooks/funnel_events/use_flow_initiated_funnel_event';
import { useTrackFunnelEvents } from '@utils/hooks/funnel_events/use_track_funnel_event';
import { reportError } from '@utils/monitoring';
import { ProtectionPlanEnum } from '@utils/protection_plan';
import {
  EventName,
  ProductType,
  createThirdPartyConversionEvent,
} from '@utils/third_party_conversion_events';

import { BookingSummary } from './booking_summary';
import { hasLeadToken, isChallengerStoragePlanComplete } from './completion';
import { ChallengerLayout } from './layout';
import { LearnMoreModal } from './learn_more_modal';
import { MobileStickyBar } from './mobile_sticky_bar';
import { PackingSection } from './sections/packing';
import { PickupSection } from './sections/pickup';
import { PlanAndCommitmentSection } from './sections/plan_and_commitment';
import { ProtectionSection } from './sections/protection';
import { StorageLocationSection } from './sections/storage_location';
import { ContentColumn, MainRow, Page, SummaryColumn } from './shared';
import { CHALLENGER as T } from './tokens';

/** Figma mobile: location / plan / protection sit in one cluster with 12px gaps. */
const PlanCluster = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[12]};
  width: 100%;
`;

export const ChallengerStoragePlanPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const track = useTrack();
  const trackFunnelEvent = useTrackFunnelEvents();
  const thirdPartyEventParams = useThirdPartyEventParams('challenger');
  const [showOverlay, setShowOverlay] = useState(false);
  const [reserving, setReserving] = useState(false);
  const initiateFlowTriggeredRef = useRef(false);
  const createLead = useCreateLead();

  const {
    data: {
      lead: { token },
      urlPromoCode,
    },
  } = useClientDataContext();

  const {
    disposalEligible,
    flowState: { getStepProps, onChange, goToStep, values },
    navigationState: { dispatch },
  } = useStorageCheckoutContext();

  const { data, loading: loadingGeocode } = useQuery(GeocodeDocument, {
    variables: { zip: values.zip! },
    skip: !values.zip,
  });
  const storageEligible = !!data?.geocode?.eligibleForDoorToDoor;

  useFlowInitiatedFunnelEvents();

  useEffect(() => {
    if (storageEligible && !initiateFlowTriggeredRef.current) {
      initiateFlowTriggeredRef.current = true;
      createThirdPartyConversionEvent(EventName.INITIATE_FLOW, {
        zip_code: values.zip,
        checkout_version: CheckoutVersion.StorageV2,
        product_type: ProductType.SMART_STORAGE,
      });
    }
  }, [storageEligible, values.zip]);

  const completionArgs = {
    values,
    disposalEligible,
    leadToken: values.leadToken || token,
    storageEligible,
    loadingGeocode,
  };
  const planComplete = isChallengerStoragePlanComplete(completionArgs);

  const stepProps = {
    ...getStepProps(),
    enabled: true,
    scrollToStep: () => undefined,
    onChange: ((key, value, cb) => {
      onChange(key, value, (...args) => {
        cb?.(...args);
      });
    }) as typeof onChange,
  };

  const goToCart = () => {
    dispatch({ type: 'viewCart' });
    history.push({
      pathname: WWW_ROUTES.STORAGE_CART,
      search: location.search,
    });
    goToStep(StorageCheckoutStep.Cart);
  };

  const handleReserve = async () => {
    if (!planComplete || reserving) return;

    // Match classic reserve_button.tsx third-party + WT click before cart.
    createThirdPartyConversionEvent(
      EventName.RESERVE_CLICKED,
      thirdPartyEventParams,
    );
    track({
      objectName: 'cart_cta',
      objectType: 'button',
      label: 'Reserve',
    });

    setReserving(true);
    let navigatedToCart = false;
    try {
      // Contact lives on Confirm (Figma Desktop 03). Only create a lead here
      // when name/email/phone are already present (e.g. returning draft).
      const hasContact =
        !!values.name?.trim() &&
        !!values.email?.trim() &&
        !!values.phone?.trim();
      if (
        hasContact &&
        !hasLeadToken({ values, leadToken: token }) &&
        !values.customerToken
      ) {
        const lead = await createLead({
          serviceContext: ServiceEnum.DoorToDoor,
          leadAttributes: {
            name: values.name,
            phone: values.phone,
            email: values.email,
            serviceNeeds: [Lead__ServiceNeeds.SmartStorage],
          },
          trackingParams: {
            zip_code: values.zip,
          },
        });
        onChange('leadToken', lead.token);
        if (lead.token && lead.id) {
          // Match classic lead_fields LeadCaptureCompleted when contact already known.
          trackFunnelEvent({
            schema: EventSchema.WWW__LeadCaptureCompleted,
            action: 'submit',
            metadata: {
              name: values.name,
              email: values.email,
              lead_id: lead.id,
            },
          });
        }
      }
      goToCart();
      navigatedToCart = true;
    } catch (error) {
      reportError(error instanceof Error ? error : new Error(String(error)), {
        tags: { component: 'ChallengerStoragePlanPage' },
      });
      // Lead create can fail (e.g. existing customer MFA). Still allow cart —
      // payment page can recreate / resolve the lead.
      goToCart();
      navigatedToCart = true;
    } finally {
      if (!navigatedToCart) {
        setReserving(false);
      }
    }
  };

  const reserveDisabled = !planComplete || reserving;

  return (
    <ChallengerLayout step="storage_plan">
      <MovingRedirectDialog />
      <Page>
        <MainRow>
          <ContentColumn>
            <WTProvider params={{ container: 'challenger_storage_plan' }}>
              <PlanCluster>
                <StorageLocationSection
                  onLearnMore={() => setShowOverlay(true)}
                />
                <PlanAndCommitmentSection
                  planSize={values.planSize}
                  commitment={values.commitment}
                  onPlanSizeChange={(plan: PlanName) =>
                    onChange('planSize', plan)
                  }
                  onCommitmentChange={(commitment: CommitmentType) =>
                    onChange('commitment', commitment)
                  }
                />
                <ProtectionSection
                  value={values.protectionPlan}
                  onChange={(plan: ProtectionPlanEnum) =>
                    onChange('protectionPlan', plan)
                  }
                />
              </PlanCluster>

              <PackingSection
                value={values.bundleKind}
                onChange={(bundle: DoorToDoor__BundleEnum) =>
                  onChange('bundleKind', bundle)
                }
                disposalSelected={values.disposalSelected}
                onDisposalChange={(selected) =>
                  onChange('disposalSelected', selected)
                }
              />

              <PickupSection stepProps={stepProps} />
            </WTProvider>
          </ContentColumn>

          <SummaryColumn>
            <BookingSummary
              disabled={reserveDisabled}
              onReserve={handleReserve}
            />
          </SummaryColumn>
        </MainRow>
      </Page>

      <MobileStickyBar disabled={reserveDisabled} onReserve={handleReserve} />

      <LearnMoreModal
        isOpen={showOverlay}
        onClose={() => setShowOverlay(false)}
      />
      <AbandonmentModal allowOpen={!urlPromoCode} />
      <CouponModal serviceSelection={ServiceSelection.Storage} />
    </ChallengerLayout>
  );
};
